<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems, seo } from '~/data/pages/banks/mocks'
import { WidgetBankPageAboutBankHeader } from '~/components/widget/bank-page'
import type { IBank } from '~/components/entity/bank'
import type { IResponseData } from '~/types'
import { WidgetSelectDepositType } from '~/components/widget/select-deposit-type'
import {
  WidgetOrganizationAsideContacts,
  mapBankToOrganizationInfo
} from '~/components/widget/organization-aside-contacts'
import { WidgetDepositsList } from '~/components/widget/deposits'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'
import { FeatureDepositFormRequest } from '~/components/feature/deposit/form-request'
import { WidgetScrollTo } from '~/components/widget/scroll-to'
import { ContentBlock } from '~/components/ui/content-block'

useSeoMeta(seo)
const route = useRoute()
const { t, locale } = useI18n()
const bankUrl = `/banks/${route.params.bank}`
const { data } = await useApiWith404<IResponseData<IBank>>(() => useUrlWithParams(bankUrl), {
  key: `banks-deposits-${route.params.bank}`
})

const bank = computed(() => data.value?.data)
useEmptySectionNoindex(bank, 'deposits_count')

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: bank.value?.title
  }
}))

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
  { title: bank.value?.title || t('common.bank'), url: `/bank/${route.params.bank}` },
  { title: t('common.deposits'), url: `/bank/${route.params.bank}/deposits` }
])

const depositCurrencies = [
  {
    id: 1,
    code: 'KGS'
  },
  {
    id: 2,
    code: 'USD'
  },
  {
    id: 3,
    code: 'EUR'
  },
  {
    id: 4,
    code: 'RUB'
  }
]

const fitDepositFormData = computed(() => ({
  organization: {
    type: bank.value?.organization_type,
    id: bank.value?.id
  }
}))

const organizationInfo = computed(() => mapBankToOrganizationInfo(bank.value))
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="organizationInfo"
              :organization="organizationInfo"
            />
            <WidgetScrollTo
              v-if="bank && bank.permissions.is_deposit_request_allowed && bank.status === 'active'"
              :title="$t('page.bank.deposits.request_form_title')"
              :description="$t('page.bank.deposits.request_form_description')"
              :button-text="$t('page.bank.deposits.request_form_button_text')"
              target="deposit-form"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectDepositType :query-params="{ 'filter[position]': 'sidebar' }" />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetBankPageAboutBankHeader :bank="bank">
            <WidgetBankServicesMenu
              v-if="bank && bank.status === 'active'"
              :organization="bank"
              :nav-items="navItems"
            />
          </WidgetBankPageAboutBankHeader>
          <WidgetSeoContent
            heading-level="h2"
            heading-class="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: bank?.official_title ?? ''
            }"
          />
          <WidgetDepositsList
            v-if="bank && bank.id"
            :hide-organization-details="true"
            :query-params="{
              per_page: '50',
              'filter[organization_id]': bank.id.toString(),
              'filter[organization_type]': bank.organization_type
            }"
            position="banks"
          />
          <ContentBlock
            v-if="bank && bank.permissions.is_deposit_request_allowed && bank.status === 'active'"
            id="deposit-form"
            variant="white"
            :heading-text="$t('page.bank.deposits.form_heading')"
            heading-tag="h2"
            heading-class="h4"
          >
            <FeatureDepositFormRequest
              :available-currencies="depositCurrencies"
              :deposit="fitDepositFormData"
              :organization="bank"
            />
          </ContentBlock>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
